import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  findStepById,
  getStepId,
  handleFirstIncompleteStep,
  handleInvalidStepId,
  handleMissingStepId,
  handleStepWithSubsteps,
  handleStepWithoutSubsteps,
  hasAnyCompletedStep,
  isIntroStep,
  isReviewStep,
  isSpecialStep,
  parseUrlStepId,
  syncUrlFromState
} from './stepperUtils';

/**
 * Synchronizes URL params with the active step/substep state, in both directions.
 * `isNavigatingProgrammatically` is set by the caller's mutation callbacks
 * (e.g. when goNext/goPrev change state) so that the URL sync defers to it.
 */
export default function useStepperUrlSync({
  syncWithUrl,
  baseUrl,
  skipInitialUrlSync = false,
  isInitialized,
  stepsState,
  flattenedSubsteps,
  activeStep,
  activeSubstep,
  activateStep,
  navigateToSubstep,
  isNavigatingProgrammatically
}) {
  const params = useParams();
  const navigate = useNavigate();
  const isNavigatingFromUrl = useRef(false);
  const justSyncedUrlFromState = useRef(false);
  const hasAppliedInitialSync = useRef(false);

  // URL -> State
  useEffect(() => {
    if (!syncWithUrl || !isInitialized || stepsState.length === 0) return;
    if (skipInitialUrlSync && !hasAppliedInitialSync.current) {
      hasAppliedInitialSync.current = true;
      return;
    }
    if (isNavigatingProgrammatically.current) return;
    if (justSyncedUrlFromState.current) {
      justSyncedUrlFromState.current = false;
      return;
    }

    const { step_id: rawStepId, substep_id } = params;
    const step_id = parseUrlStepId(rawStepId);

    if (isIntroStep(step_id)) {
      if (hasAnyCompletedStep(stepsState)) {
        handleFirstIncompleteStep({ flattenedSubsteps, stepsState, baseUrl, navigate, params });
        return;
      }

      const introStep = findStepById(stepsState, step_id);
      if (introStep) {
        if (!introStep.active) {
          isNavigatingFromUrl.current = true;
          activateStep(introStep.code, { fromUrl: true });
        }
        return;
      }
      handleMissingStepId({ flattenedSubsteps, stepsState, baseUrl, navigate, params });
      return;
    }

    if (isReviewStep(step_id)) {
      // Onboarding's review step is keyed by step_id 999; checkin / instant
      // packet review steps are keyed by url_id 'review', so a 999 lookup
      // misses and bounces navigation off review. Match either identifier.
      const reviewStep =
        findStepById(stepsState, step_id) || stepsState.find((s) => isReviewStep(getStepId(s)));
      if (reviewStep) {
        if (!reviewStep.active) {
          isNavigatingFromUrl.current = true;
          activateStep(reviewStep.code, { fromUrl: true });
        }
        return;
      }
      handleInvalidStepId({ flattenedSubsteps, stepsState, baseUrl, navigate, params });
      return;
    }

    const targetStep = findStepById(stepsState, step_id);
    if (!targetStep) {
      handleInvalidStepId({ flattenedSubsteps, stepsState, baseUrl, navigate, params });
      return;
    }

    if (targetStep.substeps?.length > 0) {
      handleStepWithSubsteps({
        targetStep,
        stepId: String(step_id),
        substepId: substep_id,
        baseUrl,
        navigate,
        navigateToSubstep,
        isNavigatingFromUrl,
        params
      });
    } else {
      handleStepWithoutSubsteps({
        targetStep,
        stepId: String(step_id),
        substepId: substep_id,
        baseUrl,
        navigate,
        activateStep,
        isNavigatingFromUrl,
        params
      });
    }
  }, [
    params,
    stepsState,
    flattenedSubsteps,
    syncWithUrl,
    skipInitialUrlSync,
    isInitialized,
    baseUrl,
    navigate,
    activateStep,
    navigateToSubstep,
    isNavigatingProgrammatically
  ]);

  // State -> URL
  useEffect(() => {
    if (!syncWithUrl || !isInitialized || !baseUrl) return;

    if (isNavigatingFromUrl.current) {
      isNavigatingFromUrl.current = false;
      return;
    }

    const stepId = getStepId(activeStep);

    if (isSpecialStep(stepId) && !isNavigatingProgrammatically.current) {
      return;
    }

    if (isNavigatingProgrammatically.current) {
      isNavigatingProgrammatically.current = false;
    }

    justSyncedUrlFromState.current = true;

    syncUrlFromState({
      activeStep,
      activeSubstep,
      params,
      baseUrl,
      navigate
    });
  }, [
    activeSubstep,
    activeStep,
    syncWithUrl,
    isInitialized,
    baseUrl,
    navigate,
    params,
    isNavigatingProgrammatically
  ]);
}
